import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import capitalize from 'lodash/capitalize';
import { makeStyles } from '@material-ui/core/styles';
import {
  Card,
  CardContent,
  Grid,
  Chip,
  Typography,
  List,
  ListItem,
  Divider,
  Slider,
  Box,
  Slide,
  ListItemSecondaryAction,
  Hidden,
  IconButton,
} from '@material-ui/core';
import ChevronLeftRoundedIcon from '@material-ui/icons/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@material-ui/icons/ChevronRightRounded';

const useStyles = makeStyles((theme) => ({
  summary: {
    [theme.breakpoints.down('sm')]: {
      position: 'relative',
      marginTop: theme.spacing(4) + 40,
      marginLeft: theme.spacing(2),
      width: '276px',
    },
    [theme.breakpoints.up('md')]: {
      maxWidth: '276px',
      position: 'absolute',
      top: theme.spacing(4) + 40, // adjust for height of search box
      left: theme.spacing(2),
    },
  },
  stormList: {
    overflowY: 'scroll',
    overflowX: 'hidden',
    '&::-webkit-scrollbar': {
      width: '5px',
      backgroundColor: 'rgba(0, 0, 0, 0)',
      WebkitBorderRadius: '100px',
    },
    '&::-webkit-scrollbar-hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.09)',
    },
    '&::-webkit-scrollbar-thumb:vertical': {
      background: '#dadada',
      WebkitBorderRadius: '100px',
    },
    '&::-webkit-scrollbar-thumb:vertical:active': {
      background: 'rgba(0, 0, 0, 0.6)',
      WebkitBorderRadius: '100px',
    },
  },
  eventListItem: {
    cursor: 'pointer',
    [theme.breakpoints.down('sm')]: {
      '&.MuiListItem-secondaryAction': {
        paddingRight: '4px !important',
      },
      '&.MuiListItem-gutters': {
        paddingLeft: '4px !important',
        paddingRight: '4px !important',
      },
    },
  },
  eventListAction: {
    '&.MuiListItemSecondaryAction-root': {
      right: '4px !important',
    },
  },
  stormListText: {
    lineHeight: 1,
  },
  eventActive: {
    color: theme.palette.secondary.main,
  },
  slider: {
    [theme.breakpoints.down('sm')]: {
      padding: '10px 0 !important',
    },
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 340,
    [theme.breakpoints.down('sm')]: {
      height: 'calc(45vh - 56px)',
      paddingTop: theme.spacing(3),
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      paddingBottom: `${theme.spacing(3)}px !important`,
    },
    [theme.breakpoints.up('md')]: {
      height: 'calc(45vh - 56px)',
      paddingTop: theme.spacing(4),
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      paddingBottom: `${theme.spacing(4)}px !important`,
    },
  },
  flexFixed: {
    flex: 0,
  },
  toggleButton: {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
    boxShadow: theme.shadows[3],
    zIndex: theme.zIndex.mobileStepper - 1,
    marginLeft: `-${theme.spacing(1)}px`,
    '&:hover': {
      backgroundColor: theme.palette.background.default,
    },
  },
  show: {
    position: 'absolute',
    left: 0,
    [theme.breakpoints.down('sm')]: {
      top: 68,
    },
    [theme.breakpoints.up('md')]: {
      top: 72,
    },
  },
  hide: {
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(3),
    },
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(4),
    },
  },
}));

const EventsSummary = ({
  dateRange,
  setDateRange,
  eventYears,
  eventProfiles,
  highlightedPathIndex,
  setHighlightedPathIndex,
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const [range, setRange] = useState([1972, 2021]);
  const [filteredEventProfiles, setFilteredEventProfiles] = useState([]);

  useEffect(() => {
    setFilteredEventProfiles(eventProfiles);
    setRange([1972, 2021]);
    setDateRange([1972, 2021]); // TODO: move to useEffect in App.js ???
  }, [eventProfiles, setRange, setDateRange]);

  useEffect(() => {
    setOpen(true);
  }, [eventProfiles]);

  const updateRangeDisplay = (e, newValues) => {
    setRange(newValues);
  };

  const updateRange = (e, newValues) => {
    setDateRange(newValues);
    let newEvents = eventProfiles.filter(
      (event) => event.year >= newValues[0] && event.year <= newValues[1]
    );
    setFilteredEventProfiles(newEvents);
  };

  const handleOpen = () => {
    setOpen(!open);
  };

  return (
    <div>
      <Slide direction='right' in={open} timeout={{ enter: 200, exit: 180 }}>
        <Box display='flex' flexDirection='row' className={classes.summary}>
          <Box flex={1} zIndex='mobileStepper'>
            <Card raised={true}>
              <CardContent className={classes.cardContent}>
                {/* <div className={classes.flexFixed}> */}
                <Box flex={0}>
                  <Box
                    display='flex'
                    flexDirection='row'
                    flexWrap='nowrap'
                    justifyContent='space-between'
                    alignItems='center'
                    pb={{ xs: 1, sm: 1.5, md: 2 }}
                  >
                    <Typography variant='h5'>Summary</Typography>
                    <Typography variant='body2' color='textSecondary'>
                      {`(${dateRange[0]} - ${dateRange[1]})`}
                    </Typography>
                  </Box>
                  <Hidden smDown>
                    <Grid
                      container
                      direction='row'
                      justify='space-between'
                      spacing={1}
                    >
                      <Grid item xs={9} zeroMinWidth>
                        <Typography className={classes.stormListText} noWrap>
                          Tropical Storms:
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Box
                          fontSize='body1.fontSize'
                          fontWeight='fontWeightBold'
                          className={classes.stormListText}
                          textAlign='right'
                        >
                          {
                            eventYears.ts.filter(
                              (year) =>
                                year >= dateRange[0] && year <= dateRange[1]
                            ).length
                          }
                        </Box>
                      </Grid>
                      <Grid item xs={9} zeroMinWidth>
                        <Typography className={classes.stormListText} noWrap>
                          Minor Hurricanes:
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Box
                          fontSize='body1.fontSize'
                          fontWeight='fontWeightBold'
                          className={classes.stormListText}
                          textAlign='right'
                        >
                          {
                            eventYears.h.filter(
                              (year) =>
                                year >= dateRange[0] && year <= dateRange[1]
                            ).length
                          }
                        </Box>
                      </Grid>
                      <Grid item xs={9} zeroMinWidth>
                        <Typography className={classes.stormListText} noWrap>
                          Major Hurricanes:
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Box
                          fontSize='body1.fontSize'
                          fontWeight='fontWeightBold'
                          className={classes.stormListText}
                          textAlign='right'
                        >
                          {
                            eventYears.mh.filter(
                              (year) =>
                                year >= dateRange[0] && year <= dateRange[1]
                            ).length
                          }
                        </Box>
                      </Grid>
                    </Grid>
                  </Hidden>
                  <Box py={{ xs: 2, sm: 2, md: 1 }}>
                    <Divider light />
                  </Box>
                  <Grid
                    container
                    direction='row'
                    justify='space-between'
                    spacing={1}
                  >
                    <Grid item xs={9} zeroMinWidth>
                      <Typography noWrap>Total:</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Box
                        fontSize='body1.fontSize'
                        fontWeight='fontWeightBold'
                        className={classes.stormListText}
                        textAlign='right'
                      >
                        {eventYears.h.filter(
                          (year) => year >= dateRange[0] && year <= dateRange[1]
                        ).length +
                          eventYears.mh.filter(
                            (year) =>
                              year >= dateRange[0] && year <= dateRange[1]
                          ).length +
                          eventYears.ts.filter(
                            (year) =>
                              year >= dateRange[0] && year <= dateRange[1]
                          ).length}
                      </Box>
                    </Grid>
                  </Grid>
                  <Box my={2}>
                    <Divider />
                  </Box>
                </Box>

                <Box flex={1} className={classes.stormList}>
                  <List>
                    {filteredEventProfiles.map((event, index) => (
                      <ListItem
                        dense
                        key={event.stormid}
                        className={classes.eventListItem}
                        onMouseEnter={() => setHighlightedPathIndex(index)}
                        onMouseLeave={() => setHighlightedPathIndex(-1)}
                      >
                        <Box
                          fontWeight='fontWeightBold'
                          py={{ xs: 0.25, sm: 0.5, md: 0.75 }}
                          pr={0.75}
                          className={
                            index === highlightedPathIndex
                              ? classes.eventActive
                              : null
                          }
                        >
                          {capitalize(event.name)}
                          <Typography
                            variant='subtitle2'
                            color='textSecondary'
                            component='span'
                          >
                            {` - ${event.year}`}
                          </Typography>
                        </Box>
                        <ListItemSecondaryAction
                          className={classes.eventListAction}
                        >
                          {/* TODO: set color depending on category to match category point on map */}
                          <Chip
                            label={`cat. ${event.category}`}
                            size='small'
                            variant='outlined'
                            color='secondary'
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>

                <Box flex={0}>
                  <Box my={2}>
                    <Divider />
                  </Box>
                  <Typography id='range-slider' align='center' gutterBottom>
                    Year Range
                  </Typography>
                  <Box px={1}>
                    <Slider
                      value={range}
                      min={1972}
                      max={2021}
                      onChange={updateRangeDisplay}
                      onChangeCommitted={updateRange}
                      color='secondary'
                      valueLabelDisplay='auto'
                      aria-labelledby='range-slider'
                      className={classes.slider}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box flex={0} zIndex={4}>
            <IconButton
              onClick={handleOpen}
              color='secondary'
              size='small'
              className={`${classes.toggleButton} ${classes.hide}`}
            >
              <ChevronLeftRoundedIcon />
            </IconButton>
          </Box>
        </Box>
      </Slide>
      <Slide
        in={!open}
        direction='right'
        timeout={{ enter: 100, exit: 50 }}
        style={{ transitionDelay: '30ms' }}
        mountOnEnter
        unmountOnExit
      >
        <IconButton
          onClick={handleOpen}
          color='secondary'
          size='small'
          className={`${classes.toggleButton} ${classes.show}`}
        >
          <ChevronRightRoundedIcon />
        </IconButton>
      </Slide>
    </div>
  );
};

EventsSummary.propTypes = {
  dateRange: PropTypes.array.isRequired,
  setDateRange: PropTypes.func.isRequired,
  eventProfiles: PropTypes.array.isRequired,
};

export default EventsSummary;
